<template>
    <FormGroup
        :name="name"
        :label="label"
        :as-row="asRow"
        :size="size"
        :required="required"
        :errors="errors"
        :hide-validation="!showValidation">
        <template #default>
            <select
                ref="input"
                v-model="model"
                class="form-control"
                :class="inputClass"
                :name="name"
                :required="required"
                v-bind="$attrs">
                <option :value="null">
                    {{ nulllUserLabel }}
                </option>

                <option
                    v-for="user in virtualUsers"
                    :key="user.id"
                    :value="user.id">
                    {{ user | username }}
                </option>

                <optgroup v-if="availableUsers.length === 0" key="empty-list" :label="emptyUsersLabel" />

                <optgroup
                    v-for="role in roles"
                    v-else
                    :key="role.id"
                    :label="role.description">
                    <option
                        v-for="user in usersByRole[role.id]"
                        :key="user.id"
                        :value="user.id">
                        {{ user | username }}
                    </option>
                </optgroup>
            </select>
        </template>

        <template #tips>
            <slot />
        </template>
    </FormGroup>
</template>

<script>
import FieldMixin from '@/js/mixins/FieldMixin';

import FormGroup from '@/js/components/common/fields/FormGroup';

export default {
    name: 'UsersComboField',

    components: {FormGroup},

    filters: {
        username(user) {
            return user.nickname || user.name;
        },
    },

    mixins: [FieldMixin],

    inheritAttrs: false,

    props: {
        value: {type: [String, Number], default: null},
        users: {type: Array, required: true},
        customUsers: {type: Array, default: () => []},
        nullLabel: {type: String, default: null},
        emptyLabel: {type: String, default: null},
    },

    computed: {
        availableUsers() {
            return this.users.filter(({id}) => id !== this.$gate.user.id);
        },

        virtualUsers() {
            return this.customUsers.filter(({id}) => {
                if (id === 'author' && this.$gate.user.id === 2) {
                    return false;
                }

                return id !== this.$gate.user.id;
            });
        },

        allUsers() {
            return this.availableUsers.concat(this.virtualUsers);
        },

        roles() {
            const roles = this.availableUsers
                .reduce((map, user) => {
                    if (!map[user.role.id]) {
                        map[user.role.id] = user.role.description;
                    }

                    return map;
                }, {});

            return Object.keys(roles)
                .map((id) => ({id: Number(id), description: roles[id]}))
                .sort((one, other) => String(one.description).toLocaleLowerCase()
                    .localeCompare(String(other.description).toLocaleLowerCase()));
        },

        usersByRole() {
            return this.roles
                .reduce((map, role) => {
                    map[role.id] = this.availableUsers
                        .filter((user) => user.role.id === role.id)
                        .sort((one, other) => {
                            const oneUsername = this.$options.filters.username(one);
                            const otherUsername = this.$options.filters.username(other);

                            return String(oneUsername).toLocaleLowerCase()
                                .localeCompare(String(otherUsername).toLocaleLowerCase());
                        });

                    return map;
                }, {});
        },

        nulllUserLabel() {
            return this.nullLabel || this.$t('common.placeholders.choose');
        },

        emptyUsersLabel() {
            return this.emptyLabel || this.$t('users.empty');
        },
    },

    methods: {
        parse(value) {
            return this.format(value);
        },

        format(value) {
            if ((value === null) || (value === undefined)) {
                return null;
            }

            if (this.allUsers.findIndex((user) => value === user.id) === -1) {
                return null;
            }

            return value;
        },
    },
};
</script>
